import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Building2, CalendarClock, Flame, Handshake, ListChecks, Phone, Search, Users } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PROP_STATUSES, STAGE_DOT, TEMP_HEX, telLink, waLink } from "@/lib/constants";
import { relDay } from "@/lib/format";
import { PageHeader, Spinner, TempBadge } from "@/components/common";
import FinanceDashboard from "@/components/FinanceDashboard";
import { cn } from "@/lib/utils";

const Kpi = ({ label, value, icon: Icon, to, tone, testid, delay }) => (
  <Link
    to={to}
    data-testid={testid}
    style={{ animationDelay: `${delay}ms` }}
    className="fade-up group rounded-2xl border bg-card p-5 transition-colors hover:border-primary/40"
  >
    <div className="flex items-center justify-between">
      <span className="label-xs">{label}</span>
      <Icon className={cn("h-4 w-4", tone || "text-muted-foreground")} />
    </div>
    <p className="num mt-3 text-3xl font-semibold tracking-tight">{value}</p>
  </Link>
);

const Card = ({ title, children, action, testid, className }) => (
  <section data-testid={testid} className={cn("rounded-2xl border bg-card p-5 fade-up", className)}>
    <div className="mb-4 flex items-center justify-between">
      <h3 className="font-display text-base font-bold">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

function TempDonut({ data }) {
  const nav = useNavigate();
  const rows = ["HOT", "WARM", "COLD"].map((t) => ({ name: t, value: data[t] || 0, color: TEMP_HEX[t] }));
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="flex items-center gap-6">
      <div className="relative h-44 w-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" innerRadius={52} outerRadius={78} paddingAngle={3} stroke="none"
              onClick={(_, i) => nav(`/leads?temperature=${rows[i].name}&stage=active`)}>
              {rows.map((r) => <Cell key={r.name} fill={r.color} cursor="pointer" />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="num text-2xl font-semibold">{total}</span>
          <span className="label-xs">active</span>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        {rows.map((r) => (
          <Link key={r.name} to={`/leads?temperature=${r.name}&stage=active`} data-testid={`dash-temp-${r.name}`} className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-secondary">
            <TempBadge t={r.name} />
            <span className="num text-sm font-semibold">{r.value}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StageFunnel({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <Link key={r.stage} to={`/leads?stage=${encodeURIComponent(r.stage)}`} data-testid={`dash-stage-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} className="group block">
          <div className="mb-1 flex justify-between text-xs">
            <span className="font-medium">{r.stage}</span>
            <span className="num text-muted-foreground">{r.count}</span>
          </div>
          <div className="h-2.5 rounded-full bg-secondary">
            <div className={cn("h-2.5 rounded-full transition-[width] duration-500", STAGE_DOT[r.stage])} style={{ width: `${Math.max(3, (r.count / max) * 100)}%` }} />
          </div>
        </Link>
      ))}
    </div>
  );
}

function PropertyStatus({ data }) {
  const Block = ({ side, label, rows, total }) => (
    <div className="rounded-xl bg-secondary/50 p-4">
      <Link to={`/properties?side=${side}`} className="flex items-baseline justify-between" data-testid={`dash-props-${side}`}>
        <span className="text-sm font-semibold">{label}</span>
        <span className="num text-xl font-semibold">{total}</span>
      </Link>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {PROP_STATUSES.map((s) => (
          <Link key={s} to={`/properties?side=${side}&status=${encodeURIComponent(s)}`} data-testid={`dash-props-${side}-${s.replace(/\s+/g, "-").toLowerCase()}`} className="flex justify-between rounded-md bg-card px-2.5 py-1.5 text-xs hover:ring-1 hover:ring-primary/40">
            <span className="text-muted-foreground">{s}</span>
            <span className="num font-semibold">{rows[s] || 0}</span>
          </Link>
        ))}
      </div>
    </div>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Block side="inventory" label="Inventory" rows={data.inventory} total={data.inventory_total} />
      <Block side="requirements" label="Requirements" rows={data.requirements} total={data.requirements_total} />
    </div>
  );
}

function FollowupList({ items, overdue }) {
  if (!items.length) return <p className="py-6 text-center text-sm text-muted-foreground">{overdue ? "Nothing overdue" : "No follow-ups today"}</p>;
  return (
    <ul className="divide-y">
      {items.map((l) => (
        <li key={l.id} className="flex items-center gap-3 py-3" data-testid={`dash-followup-${l.id}`}>
          <Link to={`/leads/${l.id}`} className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{l.name}</p>
            <p className={cn("text-xs", overdue ? "text-destructive font-medium" : "text-muted-foreground")}>{relDay(l.next_followup_at)}</p>
          </Link>
          <TempBadge t={l.temperature} />
          <a href={telLink(l.phone)} className="rounded-full border p-2 text-sky-600 hover:bg-secondary" data-testid={`dash-call-${l.id}`}><Phone className="h-4 w-4" /></a>
          <a href={waLink(l.phone)} target="_blank" rel="noreferrer" className="rounded-full border p-2 text-[#25D366] hover:bg-secondary" data-testid={`dash-wa-${l.id}`}>
            <WaIcon />
          </a>
        </li>
      ))}
    </ul>
  );
}

export const WaIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
  </svg>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const first = user?.name?.split(" ").filter((w) => !w.endsWith("."))[0];

  if (isLoading || !data) return <Spinner />;
  if (data.view === "finance") {
    return (
      <>
        <PageHeader title={`Hello, ${first}`} subtitle="Accounts view" testid="dashboard-title" />
        <FinanceDashboard />
      </>
    );
  }
  const k = data.kpis;
  return (
    <>
      <PageHeader title={`Hello, ${first}`} subtitle="Here's what needs your attention today." testid="dashboard-title" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi delay={0} testid="kpi-active-leads" label="Active leads" value={k.active_leads} icon={Users} to="/leads?stage=active" />
        <Kpi delay={40} testid="kpi-hot-leads" label="Hot leads" value={k.hot_leads} icon={Flame} tone="text-red-500" to="/leads?temperature=HOT&stage=active" />
        <Kpi delay={80} testid="kpi-followups-today" label="Follow-ups today" value={k.followups_today} icon={CalendarClock} tone="text-primary" to="/leads?followup=today" />
        <Kpi delay={120} testid="kpi-followups-overdue" label="Overdue follow-ups" value={k.followups_overdue} icon={AlertTriangle} tone="text-destructive" to="/leads?followup=overdue" />
        <Kpi delay={160} testid="kpi-available-inventory" label="Available inventory" value={k.available_inventory} icon={Building2} to="/properties?side=inventory&status=Available" />
        <Kpi delay={200} testid="kpi-open-requirements" label="Open requirements" value={k.open_requirements} icon={Search} to="/properties?side=requirements&status=Available" />
        <Kpi delay={240} testid="kpi-closed-won-month" label="Closed won (month)" value={k.closed_won_month} icon={Handshake} tone="text-emerald-500" to="/leads?stage=Closed%20Won" />
        <Kpi delay={280} testid="kpi-overdue-tasks" label="Overdue tasks" value={k.overdue_tasks} icon={ListChecks} tone="text-destructive" to={`/tasks?scope=${user.role === "employee" ? "mine" : "all"}&due=overdue`} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="Active leads by temperature" testid="dash-temperature-card"><TempDonut data={data.by_temperature} /></Card>
        <Card title="Lead pipeline" testid="dash-pipeline-card"><StageFunnel rows={data.by_stage} /></Card>
        <Card title="Today's follow-ups" testid="dash-today-followups" action={<Link to="/leads?followup=today" className="text-xs font-semibold text-primary">View all</Link>}>
          <FollowupList items={data.today_followups} />
        </Card>
        <Card title="Overdue follow-ups" testid="dash-overdue-followups" action={<Link to="/leads?followup=overdue" className="text-xs font-semibold text-primary">View all</Link>}>
          <FollowupList items={data.overdue_followups} overdue />
        </Card>
        <Card title="Listed properties" testid="dash-properties-card" className="lg:col-span-2"><PropertyStatus data={data.properties} /></Card>
      </div>
      {["admin", "partner"].includes(user.role) && <FinanceDashboard />}
    </>
  );
}
